// src/layouts/privateRoute.js
import { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Toast from '../components/shared/Toast';
import Navbar from '../components/navigation/Navbar';
import Footer from '../components/navigation/Footer';
import { logout } from '../store/slices/userSlice';
import { useDispatch } from 'react-redux';
import storageHelper from '../utils/storageHelper';

const ConditionalNavbar = () => {
  const location = useLocation();
  const isOnePagerRoute = location.pathname === '/OnePager';
  const isPreviewRoute = location.pathname.startsWith('/preview/');
  const isPublishedSiteRoute = location.pathname.startsWith('/site/');
  const FiveMinuteWebsiteRoute = location === '/5MinuteWebsite';

  if (isOnePagerRoute || isPreviewRoute || isPublishedSiteRoute || FiveMinuteWebsiteRoute) {
    return null;
  }

  return <Navbar />;
};

const ConditionalFooter = ({ isCampaignManagementSystem }) => {
  const location = useLocation();
  const isOnePagerRoute = location.pathname === '/OnePager';
  const isPreviewRoute = location.pathname.startsWith('/preview/');
  const FiveMinuteWebsiteRoute = location === '/5MinuteWebsite';

  const isPublishedSiteRoute = location.pathname.startsWith('/site/');
  const isBusinessPlanRoute = location.pathname.includes('business-plan-generator');

  // Detect mobile
  const isMobile = window.innerWidth <= 768;

  if (
    isOnePagerRoute ||
    isPreviewRoute ||
    isPublishedSiteRoute ||
    isCampaignManagementSystem ||
    FiveMinuteWebsiteRoute ||
    (isMobile && isBusinessPlanRoute)
  ) {
    return null;
  }

  return <Footer />;
};

const PrivateRoute = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isProcessingAuth, setIsProcessingAuth] = useState(true);

  // Check if the current route contains "CampaignManagementSystem"
  const isCampaignManagementSystem = location.pathname.includes('CampaignManagementSystem');

  useEffect(() => {
    const handleAuth = async () => {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get('token');
      const existingToken = storageHelper.get('authToken');
      try {
        // Handle new token from URL
        if (token) {
          // Clear any existing token
          storageHelper.remove('authToken');
          // Set the new token
          // localStorage.setItem('authToken', token);
          storageHelper.set('authToken', token);
          // Clean up URL
          window.history.replaceState({}, document.title, location.pathname);
          // Show success message
          Toast.success('Successfully logged in!');
          setIsAuthenticated(true);
        }
        // Handle existing token
        else if (existingToken) {
          // Here you can add token validation logic if needed
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth error:', error);
        // localStorage.removeItem('authToken');
        storageHelper.remove('authToken');
        dispatch(logout());
        setIsAuthenticated(false);
        Toast.error('Authentication failed');
      } finally {
        setIsProcessingAuth(false);
      }
    };

    handleAuth();
  }, [location]); // Remove isProcessingAuth from dependencies

  // Show loading state while processing authentication
  if (isProcessingAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-800"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    sessionStorage.setItem('redirectUrl', location.pathname);
    return <Navigate to="/" replace state={{ from: location, showAuthModal: true }} />;
  }

  // Render protected route content
  return (
    <>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <ConditionalNavbar />
        <div style={{ flex: 1 }}>
          <Outlet />
        </div>
        <ConditionalFooter isCampaignManagementSystem={isCampaignManagementSystem} />
      </div>
    </>
  );
};

export default PrivateRoute;
